// src/services/validationBadgeServices.js
import axios from "axios";

const apiUrl = `${process.env.REACT_APP_API_URL}/api/validationbadges`;

/* Toujours relire le token à l'appel (session actualisée) */
const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/* Helper: transforme 'uploads/validationbadge/xxx.pdf' en URL absolue */
export const toAbsoluteBadgeUrl = (url = "") => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  const api = process.env.REACT_APP_API_URL || "";
  const origin = api.replace(/\/api\/?$/i, ""); // retire "/api" si présent
  const clean = String(url).replace(/^\//, "");
  return `${origin}/${clean}`;
};

/* ========= GET ========= */

// Tous les badges
export const getAllValidationBadges = async () => {
  try {
    const { data } = await axios.get(`${apiUrl}/getAll`, {
      headers: { ...authHeaders() },
    });
    return data;
  } catch (error) {
    console.error("Erreur getAllValidationBadges:", error);
    return [];
  }
};

// Par user_id
export const getValidationBadgesByUser = async (userId) => {
  try {
    const { data } = await axios.get(`${apiUrl}/byUser/${userId}`, {
      headers: { ...authHeaders() },
    });
    return data;
  } catch (error) {
    console.error("Erreur getValidationBadgesByUser:", error);
    return [];
  }
};

// Par club_id
export const getValidationBadgesByClub = async (clubId) => {
  try {
    const { data } = await axios.get(`${apiUrl}/byClub/${clubId}`, {
      headers: { ...authHeaders() },
    });
    return data;
  } catch (error) {
    console.error("Erreur getValidationBadgesByClub:", error);
    return [];
  }
};

// === NOUVELLE FONCTION (liste paginée des utilisateurs avec badge) ===
export const getUsersWithValidationBadgesList = async ({
  q = "",
  departement = "",
  page = 1,
  limit = 50,
} = {}) => {
  try {
    const { data } = await axios.get(`${apiUrl}/users-with-badges`, {
      headers: { ...authHeaders() },
      params: { q, departement, page, limit },
    });
    // attendu: { items, total, page, limit }
    return data || { items: [], total: 0, page, limit };
  } catch (error) {
    console.error("Erreur getUsersWithValidationBadgesList:", error);
    return { items: [], total: 0, page, limit };
  }
};

export const getAllUsersWithValidationBadges = async ({
  q = "",
  departement = "",
} = {}) => {
  const PAGE_SIZE = 200;
  let page = 1;
  let all = [];
  // boucle jusqu'à dernière page
  for (;;) {
    const res = await getUsersWithValidationBadgesList({
      q,
      departement,
      page,
      limit: PAGE_SIZE,
    });
    const items = res?.items || [];
    all = all.concat(items);
    if (items.length < PAGE_SIZE) break;
    page += 1;
  }
  return all;
};

/* ========= POST ========= */

// Ajouter un badge (PDF requis - champ fichier 'badge')
export const addValidationBadge = async ({
  user_id,
  club_id,
  validation_date, // 'YYYY-MM-DD'
  file, // File (PDF)
}) => {
  try {
    const form = new FormData();
    form.append("user_id", user_id);
    form.append("club_id", club_id);
    form.append("validation_date", validation_date);
    if (file) form.append("badge", file); // IMPORTANT: 'badge' côté back

    const { data } = await axios.post(`${apiUrl}/add`, form, {
      headers: {
        ...authHeaders(),
        // laisser axios définir le boundary automatiquement
        "Content-Type": "multipart/form-data",
      },
    });
    return data;
  } catch (error) {
    console.error("Erreur addValidationBadge:", error);
    return null;
  }
};

/* ========= PUT ========= */

// Mettre à jour un badge (peut remplacer le PDF)
export const updateValidationBadge = async (
  id,
  { user_id, club_id, validation_date, file } = {}
) => {
  try {
    // Si un fichier est fourni, on envoie en multipart; sinon JSON simple
    if (file) {
      const form = new FormData();
      if (user_id !== undefined) form.append("user_id", user_id);
      if (club_id !== undefined) form.append("club_id", club_id);
      if (validation_date !== undefined)
        form.append("validation_date", validation_date);
      form.append("badge", file); // remplace le PDF

      const { data } = await axios.put(`${apiUrl}/update/${id}`, form, {
        headers: {
          ...authHeaders(),
          "Content-Type": "multipart/form-data",
        },
      });
      return data;
    } else {
      const payload = {};
      if (user_id !== undefined) payload.user_id = user_id;
      if (club_id !== undefined) payload.club_id = club_id;
      if (validation_date !== undefined)
        payload.validation_date = validation_date;

      const { data } = await axios.put(`${apiUrl}/update/${id}`, payload, {
        headers: {
          ...authHeaders(),
          "Content-Type": "application/json",
        },
      });
      return data;
    }
  } catch (error) {
    console.error("Erreur updateValidationBadge:", error);
    return null;
  }
};

/* ========= DELETE ========= */

export const deleteValidationBadge = async (id) => {
  try {
    const { data } = await axios.delete(`${apiUrl}/delete/${id}`, {
      headers: { ...authHeaders() },
    });
    return data;
  } catch (error) {
    console.error("Erreur deleteValidationBadge:", error);
    return null;
  }
};
